import { API_HEADERS } from "@api/utils";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { ApolloProvider } from "@apollo/client/react";
import { CompanionDeepLinkBridge } from "@components/companion/CompanionDeepLinkBridge";
import { AuthProvider as DescopeAuthProvider } from "@descope/react-sdk";
import {
  AccountProvider,
  AuthProvider,
  NoteUsageProvider,
  PreferencesProvider,
  WSProvider,
  CurrentUserProvider,
  PreventRecordingProvider,
  TeamProvider,
  RecordingJobTypeProvider,
  NoteMultiselectProvider,
  SyncNotesProvider,
  TwilioAPIProvider,
  TestUIProvider,
  TemplatesProvider,
} from "@hooks";
import { CompanionV1Provider } from "@hooks/use-companion";
import { CompanionV2Provider } from "@hooks/use-companion-v2";
import { createTheme, ThemeProvider } from "@mui/material";
import { IGNORED_AUTH_ERRORS } from "@scribenote/utils";
import { getConfig } from "@src/config";
import { showEmailFlowErrorToast } from "@src/utils/emailFlowErrors";
import { BrowserRouter } from "react-router";
import { IntercomProvider } from "react-use-intercom";

import {
  INTERCOM_ENABLED,
  INTERCOM_ID,
  SCRIBENOTE_GRAPHQL,
} from "../../constants";
import { WidgetProvider } from "../../context/widgetContext";
import { guardedKickOut } from "../../hooks/use-prevent-recording-interruptions";

const theme = createTheme({
  typography: {
    allVariants: {
      fontFamily: "Inter",
    },
  },
});
/** BEGIN APOLLO CLIENT CONFIG */
const httpLink = createHttpLink({
  credentials: "include",
});

// Manage global auth headers through authLink
const authLink = setContext(async (_, { headers }) => {
  // Used for rerouting to the correct API host
  // Interfaces through localstorage to allow for proper ordering of
  // hooks + context + launchdarkly user identification (needs auth to be set first)

  // don't use till csrf issue fixed
  // const URI =
  //   localStorage.getItem("SCRIBENOTE_API_HOST") + "/graphql" ||
  //   SCRIBENOTE_GRAPHQL;

  return {
    uri: SCRIBENOTE_GRAPHQL,
    headers: {
      ...headers,
      ...API_HEADERS.headers(),
    },
  };
});

// Manage global graphQLErrors through errorlink
// This works with the Error Context to broadcast messages to
// our error component. Messages are pushed into the errors list,
// which is passed to error context
const errorLink = new ErrorLink(({ error }) => {
  function kickEmOut() {
    // Check if the page has already been reloaded
    if (localStorage.getItem("already_reloaded")) {
      // If it has, stop the reload loop
      return;
    }
    // Set the flag in localStorage to indicate that the page has reloaded
    localStorage.setItem("already_reloaded", "true");

    // Unauthenticate the user in the event that auth is failing.
    localStorage.removeItem("accessCsrf");
    localStorage.removeItem("auth_expiry");
    localStorage.removeItem("user_uuid");

    // Reload the page
    window.location.reload();
  }

  if (CombinedGraphQLErrors.is(error)) {
    showEmailFlowErrorToast(error);
    error.errors.forEach(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      );

      if (IGNORED_AUTH_ERRORS.includes(message)) {
        guardedKickOut(kickEmOut);
      }

      console.log(message);
      // The CSRF session token is missing.
      // Do the same for other error types
    });
  } else {
    console.log(`[Network/Server error]: ${error}`);
  }
});

const mergePaginatedNotes = (
  existing,
  incoming,
  { args: { offset = 0 } },
) => {
  // Slicing is necessary because the existing data is
  // immutable, and frozen in development.

  // only do this on notes
  if (incoming?.notes) {
    const merged = existing ? existing.notes.slice(0) : [];
    for (let i = 0; i < incoming.notes.length; ++i) {
      merged[offset + i] = incoming.notes[i];
    }
    return {
      ...incoming,
      notes: merged,
    };
  } else {
    return incoming;
  }
};

const mergeOffsetPaginatedSessions = (
  existing,
  incoming,
  { args: { offset = 0 } },
) => {
  // Basic offset-based merge for array responses
  if (!Array.isArray(incoming)) {
    return incoming;
  }

  if (offset === 0) {
    return incoming;
  }

  const merged = existing ? existing.slice(0) : [];

  for (let i = 0; i < incoming.length; ++i) {
    merged[offset + i] = incoming[i];
  }

  return merged;
};

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        inboxNotes: {
          keyArgs: [
            "$startDate",
            "$endDate",
            "$teamUuid",
            "$userUuids",
            "$filter",
            "$jobType",
            "$searchQuery",
            "$hasBeenExported",
          ],
          merge: mergePaginatedNotes,
        },
        companionSessions: {
          keyArgs: false,
          merge: mergeOffsetPaginatedSessions,
        },
        agentSessions: {
          keyArgs: false,
          merge: mergeOffsetPaginatedSessions,
        },
      },
    },
    Organization: {
      keyFields: ["uuid"],
    },
    User: {
      keyFields: ["uuid"],
    },
    Note: {
      keyFields: ["uuid"],
    },
    Patient: {
      keyFields: ["uuid"],
    },
    Team: {
      keyFields: ["uuid"],
    },
    Account: {
      keyFields: ["uuid"],
    },
    AutoTemplate: {
      keyFields: ["uuid"],
    },
    Attachment: {
      keyFields: ["uuid"],
    },
    Template: {
      keyFields: ["uuid"],
    },
    NoteGroup: {
      keyFields: ["uuid"],
    },
    TemplateGroup: {
      keyFields: ["uuid"],
    },
    CompanionSession: {
      keyFields: ["companionSessionUuid"],
    },
  },
});

/** END APOLLO CLIENT CONFIG */

const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  cache: cache,
  credentials: "include",
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: false,
    },
  },
});

export default function Providers({ children }) {
  return (
    <>
      <ApolloProvider client={client}>
        <BrowserRouter>
          <IntercomProvider
            appId={INTERCOM_ID}
            shouldInitialize={INTERCOM_ENABLED}
            autoBoot
          >
            <WSProvider>
              <DescopeAuthProvider
                projectId={getConfig().descope.projectId}
                baseUrl={getConfig().descope.baseUrl}
              >
                <AuthProvider>
                  <AccountProvider>
                    <CurrentUserProvider>
                      <TeamProvider>
                        <TemplatesProvider>
                          <RecordingJobTypeProvider>
                            <NoteUsageProvider>
                              <PreventRecordingProvider>
                                <NoteMultiselectProvider>
                                  <SyncNotesProvider>
                                    <PreferencesProvider>
                                      <WidgetProvider>
                                        <TwilioAPIProvider>
                                          <CompanionV1Provider>
                                            <CompanionV2Provider>
                                              <CompanionDeepLinkBridge />
                                              <TestUIProvider>
                                                <ThemeProvider
                                                  theme={theme}
                                                >
                                                  {children}
                                                </ThemeProvider>
                                              </TestUIProvider>
                                            </CompanionV2Provider>
                                          </CompanionV1Provider>
                                        </TwilioAPIProvider>
                                      </WidgetProvider>
                                    </PreferencesProvider>
                                  </SyncNotesProvider>
                                </NoteMultiselectProvider>
                              </PreventRecordingProvider>
                            </NoteUsageProvider>
                          </RecordingJobTypeProvider>
                        </TemplatesProvider>
                      </TeamProvider>
                    </CurrentUserProvider>
                  </AccountProvider>
                </AuthProvider>
              </DescopeAuthProvider>
            </WSProvider>
          </IntercomProvider>
        </BrowserRouter>
      </ApolloProvider>
    </>
  );
}
